import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { type FC, useMemo, useState, useEffect } from 'react'
import {
  makeTextFieldAutoFocus,
  makeTextFieldClear,
  makeTextFieldElement,
  TextField,
} from 'shared/ui/TextField'
import {
  Button,
  IconButton,
  type MoreDropdownProps,
  SpinnerLoader,
  toastStore,
  Typography,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { logger, writeTextToClipboard } from 'shared/lib'
import { type Integration } from 'entities/Integrations'
import { useEntitiesContext } from 'entities/EntitiesExport'
import {
  type IIntegrationMeeting,
  type MeetingsProps,
  MeetingsStore,
} from 'widgets/Meetings/store/meetingsStore'
import { DropdownIntegrationActionItem } from 'widgets/DropdownIntegrationActionItem'
import styles from './styles.module.scss'

export const MeetingsContent: FC<MoreDropdownProps & MeetingsProps> = observer((props) => {
  const { contact, onCloseMenu, onClose, onAddMeetingLink, onNecessaryReconnect, integrations } =
    props
  const entities = useEntitiesContext()
  const [meetingsStore] = useState(() => new MeetingsStore({ contact }, entities))

  const {
    loading,
    search,
    selectedMeeting,
    filteredMeetings,
    selectedIntegration,
    onSelectMeeting,
    onSelectedIntegration,
    setSearch,
    onBack,
    isShowBack,
    isNecessaryReconnect,
    necessaryReconnectIntegrationKey,
    isCurrentIntegrationNeedReconnect,
  } = meetingsStore

  useEffect(() => {
    onNecessaryReconnect?.(isNecessaryReconnect)
  }, [isNecessaryReconnect])

  const handleSelectedIntegration = (integration: Integration) => {
    if (integration.connected && necessaryReconnectIntegrationKey !== integration.key) {
      onSelectedIntegration(integration)
    } else {
      onCloseMenu?.()
      onClose?.()
      uiStore.changeRoute({
        path: '/settings/integrations',
      })
    }
  }

  const getLink = (meeting: IIntegrationMeeting) => {
    if ('link' in meeting) {
      return meeting.link
    }
    return meeting.scheduling_url
  }

  const handleSelectedMeeting = (meeting: IIntegrationMeeting) => {
    onCloseMenu?.()
    onSelectMeeting(meeting)
    onAddMeetingLink?.(getLink(meeting))
  }

  const onCopy = async (link: string) => {
    try {
      await writeTextToClipboard(link)
      toastStore.add({
        title: 'Copied to clipboard',
        type: 'info',
      })
    } catch (e) {
      logger.error(e)
    }
  }

  const content = useMemo(() => {
    if (loading) {
      return (
        <div className={classNames(styles.loading)}>
          <SpinnerLoader ariaLabel={'MeetingsContent'} size={16} />
        </div>
      )
    }

    if (
      !isCurrentIntegrationNeedReconnect &&
      (selectedIntegration || !integrations?.length || search)
    ) {
      return (
        <div className={styles.list}>
          {!filteredMeetings?.length && (
            <div className={styles.wrapEmpty}>
              <Typography
                variant={'body-sm-regular'}
                color={'var(--content-primary-tertiary)'}
                ariaLabel={'No results found'}
              >
                No results found
              </Typography>
            </div>
          )}
          {filteredMeetings?.map((meeting) => (
            <Button
              key={meeting.name}
              className={styles.meetingButton}
              onClick={() => handleSelectedMeeting(meeting)}
              typeBtn={'menuItem'}
              aria-label={meeting.name}
              titleContent={
                <div className={styles.meetingTitle}>
                  <Typography
                    variant={'body-md-regular'}
                    ellipsis
                    tag={'div'}
                    className={styles.leftContent}
                    ariaLabel={'label'}
                    tooltipProps={{
                      label: meeting.name,
                      placement: 'top',
                    }}
                  >
                    {meeting.name}
                  </Typography>
                  <div className={styles.rightContent}>
                    <IconButton
                      key='copy'
                      onClick={(e) => {
                        e.stopPropagation()
                        onCopy(getLink(meeting))
                      }}
                      icon={'copy'}
                      variant={'icon'}
                      color={'secondary'}
                      size={'small'}
                      tooltipProps={{
                        label: 'Copy link',
                        placement: 'top',
                      }}
                      ariaLabel='MeetingsActionTooltip_copy_link'
                    />
                  </div>
                </div>
              }
            />
          ))}
        </div>
      )
    }

    return (
      <DropdownIntegrationActionItem
        showAllIntegrations
        integrations={integrations}
        ariaLabel={'Meetings'}
        isDefaultIntegrationBtn={false}
        necessaryReconnectIntegrationKey={necessaryReconnectIntegrationKey}
        onClickIntegrationBtn={handleSelectedIntegration}
      />
    )
  }, [
    loading,
    selectedMeeting,
    selectedIntegration,
    filteredMeetings,
    search,
    isCurrentIntegrationNeedReconnect,
  ])

  return (
    <div className={classNames(styles.wrap, 'top-dropdown-column-reverse-content')}>
      {!isCurrentIntegrationNeedReconnect && selectedIntegration && (
        <>
          <div className={styles.header}>
            <TextField
              className={styles.textInput}
              size='small'
              variant='integrated'
              ariaLabel='MeetingsSearch'
              InputProps={{
                placeholder: 'Search',
              }}
              value={search}
              onChange={setSearch}
              mainActions={[makeTextFieldAutoFocus({ withFocus: !loading })]}
              leftActions={[
                makeTextFieldElement({
                  element: isShowBack ? (
                    <IconButton
                      tag='div'
                      icon={'chevronLeft'}
                      size={'small'}
                      variant={'icon'}
                      color={'secondary'}
                      fontSize={15}
                      onClick={onBack}
                      ariaLabel='MeetingsContent_back'
                    />
                  ) : undefined,
                }),
              ]}
              rightActions={[makeTextFieldClear()]}
            />
          </div>
          <div className={styles.divider} />
        </>
      )}
      <div className={styles.content}>{content}</div>
    </div>
  )
})
